import React, {ReactElement, useEffect, useState} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "../../shared/StyledComponents";
import {CircularProgress, Fab, IconButton, TextField} from "@mui/material";
import {AskWyzeChatThreads, NewAskWyzeChat} from "./types";
import {BORDER_RADIUS_MEDIUM, CARD_SHADOW, PAGE_FRAGMENT_SM_WIDTH, PD_LG, PD_MD} from "../../shared/dimens";
import {AttachFileOutlined, SendOutlined} from "@mui/icons-material";
import {ChatThread} from "@/app-modules/messages/chat/types";
import {BaseApp} from "@/shared/BaseApp";
import {MessagesContextTrait} from "@/app-modules/messages/types";
import {AskWyzeMessagesFragment} from "./AskWyzeMessagesFragment";
import {AccountProfileContextTrait} from "../../libwyze-shared/types";

export enum AskWyzeViewMode {
  NEW_CHAT,
  CHAT,
}

export function AskWyzeView(props: {
  mode: AskWyzeViewMode,
  setMode: (mode: AskWyzeViewMode) => void,
  selectedChatThreadId?: string,
  onNewWyzeChat: (chat: NewAskWyzeChat) => void
}): ReactElement {
  const practiceId = BaseApp.CONTEXT.getTrait<AccountProfileContextTrait>().getPracticePageId();
  const myParticipantKey = BaseApp.CONTEXT.getTrait<MessagesContextTrait>().getMyParticipantKey();
  const [chatThread, setChatThread] = useState<ChatThread>();
  const [newChatMessage, setNewChatMessage] = useState<string>();
  useEffect(() => {
    (async function () {
      if (!myParticipantKey) {
        return;
      }
      if (!props.selectedChatThreadId) {
        const threads = await AskWyzeChatThreads().getOrLoadListItems();
        if (threads?.length > 0) {
          setChatThread(threads[0]);
        } else {
          setChatThread(null);
        }
      } else {
        const thread = await AskWyzeChatThreads().getOrLoadItem(props.selectedChatThreadId);
        if (thread) {
          setChatThread(thread);
        } else {
          setChatThread(null);
        }
      }
    })();
  }, [props.mode, props.selectedChatThreadId]);
  return <StyledBoxColumn key={`askwyze-${props.mode || AskWyzeViewMode.CHAT}`}
                          style={{flexGrow: 1, width: "100%", alignItems: "center", justifyContent: "center"}}>
    {chatThread !== undefined
      ? ((props.mode === AskWyzeViewMode.NEW_CHAT || chatThread === null)
          ? <StyledBoxColumn style={{position: "relative", width: "92%", maxWidth: PAGE_FRAGMENT_SM_WIDTH}}>
            <TextField
              autoFocus={true}
              value={newChatMessage}
              onChange={e => setNewChatMessage(e.target.value)}
              placeholder={"Ask me anything..."}
              size={"small"} multiline={true} minRows={4} variant={"outlined"} InputProps={{
              style: {
                padding: PD_LG,
                paddingBottom: 72,
                borderRadius: BORDER_RADIUS_MEDIUM,
                boxShadow: CARD_SHADOW,
              },
            }}/>
            <StyledBoxRow
              style={{
                height: 72,
                padding: PD_MD,
                position: "absolute",
                width: "100%",
                bottom: 0,
                alignItems: "center"
              }}>
              <IconButton>
                <AttachFileOutlined/>
              </IconButton>
              <StyledSpan/>
              <Fab
                disabled={!(newChatMessage?.trim().length > 0)}
                onClick={() => {
                  setChatThread(undefined);
                  const chat = NewAskWyzeChat.createNew(newChatMessage);
                  setNewChatMessage("");
                  props.onNewWyzeChat(chat);
                }}
                color={"secondary"} variant={"circular"} size={"small"}>
                <SendOutlined/>
              </Fab>
            </StyledBoxRow>
          </StyledBoxColumn>
          : <AskWyzeMessagesFragment
            key={`askwyze-thread-${chatThread.id}`}
            onNewChat={() => props.setMode(AskWyzeViewMode.NEW_CHAT)}
            practiceId={practiceId}
            thread={chatThread}
            myParticipantKey={myParticipantKey}
            onThreadDeleted={() => props.setMode(AskWyzeViewMode.NEW_CHAT)}/>
      )
      : <CircularProgress/>}
  </StyledBoxColumn>;
}
